import { Injectable } from '@angular/core';
import { AuthUser } from '../dtos/authentication/auth_user';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  Subject,
  throwError,
  catchError,
} from 'rxjs';
import { User } from '../models/authentication/user';
import { HttpMethod } from '../constants/http-method';
import { environment } from '../../environments/environment';
import { LoginDto } from '../components/authentication/dtos/login-dto';
import { LocalStorageService } from './common/local-storage.service';
import { ApiClientService } from './common/api-client.service';
import { UpdateProfileDto } from '../components/main/profile/dtos/update-profile-dto';
import { ChangePasswordDto } from '../components/main/profile/dtos/change-password-dto';
import { ForceChangePasswordDto } from '../components/authentication/dtos/force-change-password-dto';
import { SiteService } from './site.service';
import { Site } from '../models/site/site';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _authUserSubjectBehavior: BehaviorSubject<AuthUser> =
    new BehaviorSubject<AuthUser>(new AuthUser());
  public authUser = this._authUserSubjectBehavior.asObservable();

  private _logoutEventSubject: Subject<boolean> = new Subject<boolean>();
  public logoutEvent = this._logoutEventSubject.asObservable();

  private readonly _KEY_AUTH_USER = 'app_auth_user';
  private readonly _KEY_TOKEN_FCM = 'token_fcm';

  constructor(
    private localStorageService: LocalStorageService,
    private apiClientService: ApiClientService,
    private readonly siteService: SiteService,
  ) {
    this.initAuthUser();
  }

  private initAuthUser() {
    const data = this.localStorageService.getData(this._KEY_AUTH_USER);
    if (data) {
      this._authUserSubjectBehavior.next(this.castToAuthUser(data));
    }
  }

  /**
   * Garantit que l'objet possède les méthodes de la classe AuthUser et User
   */
  private castToAuthUser(data: any): AuthUser {
    const instance = Object.assign(new AuthUser(), data);
    if (instance.user) {
      instance.user = Object.assign(new User(), instance.user);
    }
    return instance;
  }

  public getAuthUser(): AuthUser {
    return this._authUserSubjectBehavior.getValue();
  }

  public notifyOfLogoutAction(): void {
    this._logoutEventSubject.next(true);
  }

  public saveAndNotifyAuthUser(authUser: AuthUser) {
    const cleanInstance = this.castToAuthUser(authUser);
    this.localStorageService.saveData(this._KEY_AUTH_USER, cleanInstance);
    this._authUserSubjectBehavior.next(cleanInstance);
  }

  /**
   * CORRECTION : Met à jour l'utilisateur dans le cache sans perdre les tokens
   */
  public updateUserInfosInCache(user: User): void {
    const currentAuth = this._authUserSubjectBehavior.getValue();
    const updatedAuth = this.castToAuthUser(currentAuth);
    // On s'assure de garder les tokens actuels tout en mettant à jour l'objet user
    updatedAuth.user = Object.assign(new User(), user);
    this.saveAndNotifyAuthUser(updatedAuth);
  }

  public login(loginDTO: LoginDto): Observable<User> {
    return this.apiClientService
      .execute(
        environment.BASE_URL_API + '/auth/login',
        HttpMethod.POST,
        loginDTO,
      )
      .pipe(map((response: any) => this.handleLoginResponse(response.data)));
  }

  public requestNewAccessToken(): Observable<AuthUser> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearDataBeforeLogout();
      return throwError(() => new Error('Refresh token not available'));
    }

    return this.apiClientService
      .execute(
        environment.BASE_URL_API + '/auth/refresh-token',
        HttpMethod.POST,
        { refresh: refreshToken },
      )
      .pipe(
        map((data: any) => {
          const currentAuthUser = this.castToAuthUser(this.getAuthUser());
          currentAuthUser.access_token = data.access;
          if (data.refresh) {
            currentAuthUser.refresh_token = data.refresh;
          }
          this.saveAndNotifyAuthUser(currentAuthUser);
          return currentAuthUser;
        }),
        catchError((error) => {
          this.clearDataBeforeLogout();
          this.notifyOfLogoutAction();
          return throwError(() => error);
        }),
      );
  }

  public forceChangePassword(data: ForceChangePasswordDto): Observable<User> {
    return this.apiClientService
      .execute(
        environment.BASE_URL_API + '/auth/force-change-password',
        HttpMethod.POST,
        data,
      )
      .pipe(
        map((response: any) => {
          const user = Object.assign(new User(), response.data || response);
          this.updateUserInfosInCache(user);
          return user;
        }),
      );
  }

  private handleLoginResponse(data: any): User {
    const authUser = new AuthUser();
    authUser.access_token = data.tokens.access;
    authUser.refresh_token = data.tokens.refresh;
    authUser.user = Object.assign(new User(), data.user);
    authUser.user.active = data.user.is_active;
    authUser.user.id = data.user.id.toString();

    this.saveAndNotifyAuthUser(authUser);

    if (authUser.isAdminSite && authUser.user.site_name) {
      const site = new Site();
      site.nom = authUser.user.site_name;
      this.siteService.setCurrentSiteSelected(Object.assign(new Site(), site));
    }
    return authUser.user;
  }

  /**
   * RÉCUPÉRATION : Met à jour le cache avec les données fraîches du serveur
   */
  public currentUser(): Observable<User> {
    return this.apiClientService
      .execute(environment.BASE_URL_API + '/auth/current-user', HttpMethod.POST)
      .pipe(
        map((response: any) => {
          const user = Object.assign(new User(), response.data || response);
          this.updateUserInfosInCache(user);
          return user;
        })
      );
  }

  /**
   * CORRECTION : Après mise à jour du profil, on synchronise le cache local
   */
  public updateUserProfile(updateUser: UpdateProfileDto): Observable<User> {
    return this.apiClientService
      .execute(
        environment.BASE_URL_API + '/auth/update',
        HttpMethod.POST,
        updateUser,
      )
      .pipe(
        map((response: any) => {
          const user = Object.assign(new User(), response.data || response);
          this.updateUserInfosInCache(user); // Important pour la Sidebar
          return user;
        })
      );
  }

  /**
   * CORRECTION : Après changement de mot de passe, on synchronise aussi
   */
  public changePassword(changePassword: ChangePasswordDto): Observable<User> {
    return this.apiClientService
      .execute(
        environment.BASE_URL_API + '/auth/change-password',
        HttpMethod.POST,
        changePassword,
      )
      .pipe(
        map((response: any) => {
          const user = Object.assign(new User(), response.data || response);
          this.updateUserInfosInCache(user);
          return user;
        })
      );
  }

  public isLoggedIn(): boolean {
    return this.getAccessToken().length > 0;
  }

  public logout(): Observable<boolean> {
    return this.logoutOnRemote().pipe(
      finalize(() => {
        this.clearDataBeforeLogout();
        this._authUserSubjectBehavior.next(new AuthUser());
        this.notifyOfLogoutAction();
      }),
    );
  }

  public logoutOnRemote(): Observable<boolean> {
    return this.apiClientService
      .execute(environment.BASE_URL_API + '/auth/logout', HttpMethod.POST)
      .pipe(map(() => true));
  }

  // --- Helpers Stockage ---

  public getAccessToken(): string {
    return this._authUserSubjectBehavior.getValue().access_token ?? '';
  }

  public getRefreshToken(): string {
    return this._authUserSubjectBehavior.getValue().refresh_token ?? '';
  }

  public clearDataBeforeLogout() {
    const fcmToken = this.getTokenFCM();
    this.localStorageService.clear();
    this.saveTokenFCM(fcmToken);
  }

  public getTokenFCM(): string {
    return this.localStorageService.getData(this._KEY_TOKEN_FCM, false) ?? '';
  }

  public saveTokenFCM(tokenFCM: string) {
    this.localStorageService.saveData(this._KEY_TOKEN_FCM, tokenFCM, false);
  }
}